import { RouterProvider, useLocation, Outlet, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "sonner";

import { privateRoutes, publicRoutes } from "./routes";
import PrivateLayout from "@layouts/PrivateLayout";
import PublicLayout from "@layouts/PublicLayout";
import ErrorBoundary from "./exceptionHandlers/ErrorBoundry";
import { useEffect } from "react";
import { initSmartlook } from "./smartLook";
import {
  createSentryBrowserRouter,
  initSentryReactRouter,
} from "./utils/sentryReactRouter";
import RequirePermission from "@/shared/RequirePermission";
import InitialRedirect from "@/shared/InitialRedirect";

import AppDataLoader from "@/components/AppDataLoader";
import UnsavedChangesToast from "@shared/UnsavedChangesToast";

const smartLookKey = import.meta.env.VITE_SMARTLOOK_ID;

// Initialize Sentry React Router integration
initSentryReactRouter();

const ConditionalAppDataLoader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const publicPaths = publicRoutes.map((route) => route.path);
  const isPublicRoute = publicPaths.includes(location.pathname);

  // Check for invitation links that may be pointing to wrong paths (e.g., /dashboard/ instead of /user-invite/)
  // This runs BEFORE any auth check to ensure invitation params are preserved
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const acceptParam = searchParams.get("accept");
    const tokenParam = searchParams.get("token");
    const emailParam = searchParams.get("email");

    // If this looks like an invitation link but we're not on /user-invite/, redirect there
    if (acceptParam === "True" && tokenParam && !location.pathname.includes("/user-invite")) {
      const inviteUrl = `/user-invite/?accept=True&email=${encodeURIComponent(emailParam || "")}&token=${tokenParam}`;
      console.log("Redirecting invitation link to:", inviteUrl);
      navigate(inviteUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  if (isPublicRoute) {
    return <Outlet />;
  }

  return (
    <AppDataLoader>
      <Outlet />
    </AppDataLoader>
  );
};

// Create router with Sentry instrumentation
const router = createSentryBrowserRouter([
  {
    path: "/",
    element: <ConditionalAppDataLoader />,
    children: [
      {
        index: true,
        element: (
          <PrivateLayout>
            <ErrorBoundary>
              <InitialRedirect />
            </ErrorBoundary>
          </PrivateLayout>
        ),
      },
      ...privateRoutes.map((route) => {
        const requires = (route as any).permission;
        const children = <route.component />;
        const wrapped = requires ? (
          <RequirePermission
            resource={requires.resource}
            action={requires.action}
            subResource={requires.subResource}
            allOf={requires.allOf}
            anyOf={requires.anyOf}
          >
            {children}
          </RequirePermission>
        ) : (
          children
        );

        return {
          path: route.path,
          element: (
            <PrivateLayout>
              <ErrorBoundary>{wrapped}</ErrorBoundary>
              <UnsavedChangesToast />
            </PrivateLayout>
          ),
        };
      }),
      ...publicRoutes.map((route) => {
        return {
          path: route.path,
          element: (
            <PublicLayout>
              <ErrorBoundary>
                <route.component />
              </ErrorBoundary>
            </PublicLayout>
          ),
        };
      }),
    ],
    errorElement: (
      <ErrorBoundary>
        <div>Navigation Error</div>
      </ErrorBoundary>
    ),
  },
], {
  future: {
    v7_startTransition: true,
  },
});

const App = () => {
  useEffect(() => {
    if (smartLookKey) {
      initSmartlook(smartLookKey);
    }
  }, []);

  // Debug: Log when App renders
  useEffect(() => {
    console.log("App component mounted");
    console.log("Current URL:", window.location.href);
    console.log("Window width:", window.innerWidth);
    console.log("User agent:", navigator.userAgent);
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer />
      <Toaster position="top-right" richColors />
    </>
  );
};

export default App;
